import { useEffect, useContext } from 'react';

import type { PageSegmentLoadMetric } from '@atlassian/browser-metrics/types';

import { fg } from '@confluence/feature-gating';
import {
	ExperienceTrackerContext,
	SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker';

interface UseCommentsPanelMetricsProps {
	loading: boolean;
	error: Error | undefined;
	allCommentsRendered: boolean;
	commentBatchRendered: boolean;
	totalComments: number | undefined;
	contentId: string | undefined;
	pageMode: string;
	experienceName: string;
	metric: PageSegmentLoadMetric;
}

export const useCommentsPanelMetrics = ({
	loading,
	error,
	allCommentsRendered,
	commentBatchRendered,
	totalComments,
	contentId,
	pageMode,
	experienceName,
	metric,
}: UseCommentsPanelMetricsProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	// Track the start of comments loading and initialize performance metrics
	useEffect(() => {
		if (loading && fg('confluence_frontend_comments_panel_view_exp')) {
			experienceTracker.start({
				name: experienceName,
				attributes: {
					contentId,
					pageMode,
				},
			});

			metric.start();
			metric.mark('viewRenderStart');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, experienceName]);

	// Track any errors that occur during comments loading
	useEffect(() => {
		if (error && fg('confluence_frontend_comments_panel_view_exp')) {
			experienceTracker.fail({
				name: experienceName,
				error,
			});

			// Fail the overall experience if it's running
			experienceTracker.fail({
				name: SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE,
				error,
			});

			metric.cancel();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error, experienceName]);

	// Track successful completion of comments rendering
	useEffect(() => {
		if (allCommentsRendered && fg('confluence_frontend_comments_panel_view_exp')) {
			experienceTracker.succeed({
				name: experienceName,
				attributes: {
					totalComments: totalComments ?? 0,
				},
			});

			// Succeed the overall experience if it's running
			experienceTracker.succeed({
				name: SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE,
				attributes: {
					totalComments: totalComments ?? 0,
				},
			});

			metric.mark('viewRenderEnd');
			metric.stop({
				customData: {
					contentId: contentId || '',
					totalComments: totalComments ?? 0,
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allCommentsRendered, experienceName]);

	// Track successful completion of first comment batch rendering
	useEffect(() => {
		if (commentBatchRendered && fg('confluence_frontend_comments_panel_view_exp')) {
			metric.markFMP();
			metric.mark('tti');

			const experienceState = experienceTracker.getExperienceState(experienceName);

			if (experienceState?.startTime && typeof experienceState.startTime === 'number') {
				experienceTracker.updateAttributes({
					name: experienceName,
					attributes: {
						firstMeaningfulPaint: window.performance.now() - experienceState.startTime,
					},
				});

				// Update the overall experience if it's running
				experienceTracker.updateAttributes({
					name: SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE,
					attributes: {
						firstMeaningfulPaint: window.performance.now() - experienceState.startTime,
					},
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [commentBatchRendered, experienceName]);

	// Handle case when there are no comments to render
	useEffect(() => {
		if (totalComments === 0 && fg('confluence_frontend_comments_panel_view_exp')) {
			experienceTracker.abort({
				name: experienceName,
				reason: 'No comments',
			});

			metric.cancel();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [totalComments, experienceName]);
};
